import React, { useEffect, useState, useCallback } from "react";
import * as Sentry from "@sentry/react";
import styled from "styled-components/macro";
import Auth from "../../Auth.js";
import { applyTheme } from "../../lib/Theme";
import { Card } from "../Card";
import {
  Logo,
  ContentWrapper,
  BackArrow,
  BackButton,
  PageWrapper,
  ErrorMessage,
  FieldGroup,
  FieldLabel,
  EmailValue,
} from "../PageShell";
import { SudoButton } from "../../sudokit/components/SudoButton";

export default function VatConsumerSelfCert({ user, openLogin }) {
  const [loading, setLoading] = useState(true);
  const [hasSubscription, setHasSubscription] = useState(false);
  const [hasVatId, setHasVatId] = useState(false);
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);

  useEffect(() => {
    applyTheme("sorbet");
  }, []);

  const fetchInfo = useCallback(async () => {
    setLoading(true);
    setErrorMessage(null);
    try {
      const result = await Auth.fetchWithIdToken(
        "/api/vat-consumer-self-cert/info",
        {}
      );
      if (result && result.success) {
        setHasSubscription(true);
        setHasVatId(!!result.hasVatId);
        setEmail((result.customer && result.customer.email) || "");
      } else {
        setHasSubscription(false);
      }
    } catch (e) {
      Sentry.captureException(e, { tags: { feature: "vat-self-cert.info" } });
      setHasSubscription(false);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    if (user) {
      setLoading(true);
      fetchInfo();
    } else {
      setLoading(false);
    }
  }, [user, fetchInfo]);

  const handleConfirm = async () => {
    if (submitting) return;
    setSubmitting(true);
    setErrorMessage(null);
    try {
      const result = await Auth.fetchWithIdToken(
        "/api/vat-consumer-self-cert/confirm",
        {}
      );
      if (result && result.success) {
        setConfirmed(true);
      } else if (result && result.hasVatId) {
        // Server-side defense-in-depth: a VAT ID was added between page-load
        // and Confirm — re-route into the off-ramp branch.
        setHasVatId(true);
      } else {
        setErrorMessage(
          "We couldn't process your confirmation. Please try again."
        );
      }
    } catch (err) {
      Sentry.captureException(err, {
        tags: { feature: "vat-self-cert.confirm" },
      });
      setErrorMessage(
        "We couldn't process your confirmation. Please try again."
      );
    }
    setSubmitting(false);
  };

  if (!user) {
    return (
      <PageWrapper>
        <BackButton href="/app">
          <BackArrow />
          Back to Projects
        </BackButton>
        <ContentWrapper>
          <SorbetCard>
            <Logo />
            <Title>Confirm Your Status</Title>
            <Message>Please log in to confirm your tax status.</Message>
            <SudoButton variant="primary" onClick={() => openLogin && openLogin()}>
              Log In
            </SudoButton>
          </SorbetCard>
        </ContentWrapper>
      </PageWrapper>
    );
  }

  if (confirmed) {
    return (
      <PageWrapper>
        <BackButton href="/app">
          <BackArrow />
          Back to Projects
        </BackButton>
        <ContentWrapper>
          <SorbetCard>
            <Logo />
            <Title>You're all set</Title>
            <Message>
              Thanks for confirming. We'll apply VAT to your future invoices,
              and you won't see further reminders about this.
            </Message>
          </SorbetCard>
        </ContentWrapper>
      </PageWrapper>
    );
  }

  if (loading) {
    return (
      <PageWrapper>
        <BackButton href="/app">
          <BackArrow />
          Back to Projects
        </BackButton>
        <ContentWrapper>
          <SorbetCard>
            <Logo />
            <Title>Confirm Your Status</Title>
            <Message>Loading...</Message>
          </SorbetCard>
        </ContentWrapper>
      </PageWrapper>
    );
  }

  if (!hasSubscription) {
    return (
      <PageWrapper>
        <BackButton href="/app">
          <BackArrow />
          Back to Projects
        </BackButton>
        <ContentWrapper>
          <SorbetCard>
            <Logo />
            <Title>Confirm Your Status</Title>
            <InfoMessage>
              No billing account found. Please contact support if you believe
              this is an error.
            </InfoMessage>
          </SorbetCard>
        </ContentWrapper>
      </PageWrapper>
    );
  }

  if (hasVatId) {
    return (
      <PageWrapper>
        <BackButton href="/app">
          <BackArrow />
          Back to Projects
        </BackButton>
        <ContentWrapper>
          <SorbetCard>
            <Logo />
            <Title>VAT ID on file</Title>
            <Message>
              You have a VAT ID on file. To switch to consumer status, remove
              the VAT ID first.
            </Message>
            <SudoButton variant="primary" as="a" href="/pages/tax-id">
              Manage VAT ID
            </SudoButton>
          </SorbetCard>
        </ContentWrapper>
      </PageWrapper>
    );
  }

  const ctaLabel = submitting ? "Confirming..." : "I don't use Sudowrite for business";

  return (
    <PageWrapper>
      <BackButton href="/app">
        <BackArrow />
        Back to Projects
      </BackButton>
      <ContentWrapper>
        <SorbetCard>
          <Logo />
          <Title>Confirm Your Status</Title>

          <FieldGroup>
            <FieldLabel>Email</FieldLabel>
            <EmailValue>{email || user.email || "Not set"}</EmailValue>
          </FieldGroup>

          <Message>
            By confirming, you're declaring you don't use Sudowrite for
            business purposes. We'll apply VAT on future invoices and stop
            asking for a VAT ID.
          </Message>

          {errorMessage && <ErrorMessage>{errorMessage}</ErrorMessage>}

          <SudoButton
            variant="primary"
            type="button"
            onClick={handleConfirm}
            disabled={submitting}
          >
            {ctaLabel}
          </SudoButton>
        </SorbetCard>
      </ContentWrapper>
    </PageWrapper>
  );
}

const SorbetCard = styled(Card)`
  width: 100%;
  max-width: 600px;
  background: var(--menu-color, #fff);
  padding: 40px;
  text-align: left;
  display: flex;
  flex-direction: column;
  gap: 16px;

  @media (max-width: 640px) {
    padding: 24px 16px;
  }
`;

const Title = styled.h1`
  font-size: 24px;
  font-weight: 400;
  font-family: "GT Super Text", serif;
  color: var(--grey-0, #383838);
  margin: 0 0 8px 0;
`;

const Message = styled.p`
  font-size: 15px;
  color: var(--grey-1, #686868);
  margin: 0;
  line-height: 1.5;
`;

const InfoMessage = styled.p`
  font-size: 15px;
  color: var(--grey-1, #686868);
  background: var(--menu-color, #f9f9fb);
  border: 1px solid var(--grey-5, #e0e0e0);
  border-radius: 10px;
  padding: 14px 18px;
  margin: 0;
`;
